// Builder v3, ported from binaryblocks: block roles for the v3 sheet, dock tooltips, and the phone shell.
// Every phone control proxies an existing one (Start/Stop, Bots, Copy, toolbox buttons, language list), so
// trading, auth and dialogs keep running through the handlers View.js already binds.
import { translate } from '../../common/i18n';

const STAGES = ['trade', 'before_purchase', 'during_purchase', 'after_purchase'];
const STAGE_TITLE = {
    trade          : 'Trade Definition', // the toolbox sub-category name, already translated
    before_purchase: 'Before Purchase',
    during_purchase: 'During Purchase',
    after_purchase : 'After Purchase',
};
// toolbox category whose blocks are trading steps (everything else is generic logic)
const TRADE_CATS = ['Binary'];

const $id = id => document.getElementById(id);
// injected toolbox names are translated; i18n-text keeps the English key
const catKey = c => c.getAttribute('i18n-text') || c.getAttribute('name');

// ---------- block roles: stage frame / trading step / generic logic / literal (inherits its slot) ----------
const paintBlocks = toolboxDom => {
    const tradeTypes = new Set();
    [...toolboxDom.children]
        .filter(c => c.tagName === 'category' && TRADE_CATS.includes(catKey(c)))
        .forEach(c => c.querySelectorAll('block').forEach(b => tradeTypes.add(b.getAttribute('type'))));
    const roleOf = block => {
        if (STAGES.includes(block.type) || block.type === 'tick_analysis') return 'bb3-stage';
        const literal = block.outputConnection && !block.inputList.some(input => input.connection);
        if (literal) return null;
        return tradeTypes.has(block.type) ? 'bb3-trade' : 'bb3-logic';
    };
    const paint = block => {
        const g = block.getSvgRoot && block.getSvgRoot();
        if (!g) return;
        g.classList.remove('bb3-stage', 'bb3-trade', 'bb3-logic');
        const role = roleOf(block);
        if (role) g.classList.add(role);
    };
    const { setColour } = Blockly.BlockSvg.prototype;
    Blockly.BlockSvg.prototype.setColour = function setColourV3(colour) {
        const result = setColour.call(this, colour);
        paint(this);
        return result;
    };
    Blockly.mainWorkspace.getAllBlocks().forEach(paint);

    // legend dots on the category list (same order as the toolbox XML)
    const names = [...toolboxDom.children].filter(c => c.tagName === 'category').map(catKey);
    document.querySelectorAll('.blocklyTreeRoot > [role=group] > [role=treeitem]').forEach((item, i) => {
        const label = item.querySelector('.blocklyTreeLabel');
        if (label && names[i] && names[i] !== 'Quick Settings') {
            label.classList.add(TRADE_CATS.includes(names[i]) ? 'bb3-dot-trade' : 'bb3-dot-logic');
        }
    });
};

// ---------- dock: instant tooltips that keyboard focus also shows (a native title waits ~1s, never on focus) ----------
const dockTooltips = () => {
    document.querySelectorAll('#toolbox button[title]').forEach(btn => {
        const label = btn.getAttribute('title');
        if (!label) return;
        btn.setAttribute('data-tip', label);
        if (!btn.textContent.trim()) btn.setAttribute('aria-label', label);
        btn.removeAttribute('title');
    });
};

// ---------- phone shell ----------
let toastTimer;
const toast = (message, undo) => {
    const el = $id('bb3-toast');
    el.firstElementChild.textContent = message;
    const btn = el.lastElementChild;
    btn.hidden = !undo;
    btn.onclick = () => {
        if (undo) undo();
        el.classList.remove('on');
    };
    el.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('on'), 4000);
};

const sheets = () => {
    document.querySelectorAll('.bb3-sheet').forEach(d => {
        d.addEventListener('click', e => e.target === d && d.close()); // backdrop tap
        d.querySelector('.bb3-x').addEventListener('click', () => d.close());
    });
};

const startStop = () => {
    const go = $id('bb3-m-go');
    const run = $id('runButtonBottom');
    const stop = $id('stopButtonBottom');
    const shown = el => el && el.style.display !== 'none';
    const sync = () => {
        const running = shown(stop);
        go.classList.toggle('stop', running);
        go.disabled = !running && !shown(run); // logged out: View.js hides Start
        go.lastElementChild.textContent = translate(running ? 'Stop' : 'Start');
        go.firstElementChild.innerHTML = running
            ? '<rect x="6" y="6" width="12" height="12" rx="2"/>'
            : '<path d="M7 4v16l13-8z"/>';
    };
    // same path the summary panel uses: trigger the real buttons
    go.addEventListener('click', () => $(go.classList.contains('stop') ? '#stopButtonBottom' : '#runButtonBottom').trigger('click'));
    const observer = new MutationObserver(sync);
    [run, stop].forEach(el => el && observer.observe(el, { attributes: true, attributeFilter: ['style'] }));
    sync();
};

let currentStage = 0;
const stageBlocks = () => {
    const tops = Blockly.mainWorkspace.getTopBlocks();
    return STAGES.map(type => tops.find(b => b.type === type)).filter(Boolean);
};

const stageNav = () => {
    const nav = $id('bb3-stages');
    const render = () => {
        const stages = stageBlocks();
        if (currentStage >= stages.length) currentStage = 0;
        nav.innerHTML = '';
        stages.forEach((block, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = translate(STAGE_TITLE[block.type]);
            btn.setAttribute('aria-current', i === currentStage);
            btn.addEventListener('click', () => {
                currentStage = i;
                Blockly.mainWorkspace.centerOnBlock(block.id);
                render();
            });
            nav.appendChild(btn);
        });
    };
    let pending;
    Blockly.mainWorkspace.addChangeListener(e => {
        if (e.type !== Blockly.Events.CREATE && e.type !== Blockly.Events.DELETE) return;
        clearTimeout(pending);
        pending = setTimeout(render, 50);
    });
    render();
};

// Blocks sheet: categories from the real toolbox; tap a block to snap it into the current stage
const blocksSheet = toolboxDom => {
    const dialog = $id('bb3-blocks');
    const cats = dialog.querySelector('.bb3-bcats');
    const list = dialog.querySelector('.bb3-blist');
    const hint = dialog.querySelector('.bb3-hint');
    const SHAPE =
        '<svg class="shape" viewBox="0 0 34 22" aria-hidden="true"><path d="M3 1h7l2 3h6l2-3h11a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z"/></svg>';
    const PLUS = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

    // label = the block's own text, read once from a headless copy
    const labels = {};
    const labelOf = xml => {
        const type = xml.getAttribute('type');
        if (!(type in labels)) {
            const ws = new Blockly.Workspace();
            Blockly.Events.disable();
            try {
                labels[type] = Blockly.Xml.domToBlock(xml, ws).toString(40);
            } catch (e) {
                // some blocks only build inside the real workspace: fall back to a readable type name
            }
            if (!labels[type]) {
                const words = type.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
                labels[type] = words.charAt(0).toUpperCase() + words.slice(1);
            }
            Blockly.Events.enable();
            ws.dispose();
        }
        return labels[type];
    };
    const blocksIn = category =>
        [...category.querySelectorAll('block')]
            .filter(b => b.parentElement.tagName === 'category' && !STAGES.includes(b.getAttribute('type')))
            .map(xml => ({
                xml,
                sub : xml.parentElement === category ? '' : catKey(xml.parentElement),
                role: TRADE_CATS.includes(catKey(category)) ? 'trade' : 'logic',
            }));
    const categories = [...toolboxDom.children].filter(
        c => c.tagName === 'category' && c.querySelector('block')
    );
    let selected = 'stage';

    const add = ({ xml }) => {
        dialog.close();
        const ws = Blockly.mainWorkspace;
        const stage = stageBlocks()[currentStage];
        Blockly.Events.setGroup(true);
        const block = Blockly.Xml.domToBlock(xml, ws);
        let placed = false;
        if (stage && block.previousConnection) {
            // stage slots are unchecked, so prefer the slot already holding a block of the same type or connection type
            // (Trade options -> SUBMARKET, not "Run once at start")
            const wanted = block.previousConnection.check_ || [];
            const holdsKind = input => {
                for (let b = input.connection.targetBlock(); b; b = b.getNextBlock()) {
                    const kinds = b.previousConnection.check_ || [];
                    if (b.type === block.type || kinds.some(t => wanted.includes(t))) return 1;
                }
                return 0;
            };
            const slots = stage.inputList.filter(input => input.type === Blockly.NEXT_STATEMENT && input.connection);
            // a block kind the stage already holds goes only to that slot; full slot -> beside, not into an unrelated one
            const ownSlots = slots.filter(holdsKind);
            placed = (ownSlots.length ? ownSlots : slots).some(input => {
                let target = input.connection;
                let last = target.targetBlock();
                while (last && last.nextConnection) {
                    target = last.nextConnection;
                    last = target.targetBlock();
                }
                // full slot whose last block has no next connector (Trade options): connecting would bump a block out
                if (target.targetBlock() || !target.checkType_(block.previousConnection)) return false;
                target.connect(block.previousConnection);
                return true;
            });
        }
        if (!placed && stage) {
            // value blocks (and blocks the stage refuses) land beside the stage, ready to drag into a slot
            const at = stage.getRelativeToSurfaceXY();
            const size = stage.getHeightWidth();
            block.moveBy(at.x + size.width + 24, at.y);
        }
        Blockly.Events.setGroup(false);
        block.select();
        ws.centerOnBlock(block.id);
        const name = labelOf(xml);
        const where = stage ? translate(STAGE_TITLE[stage.type]) : '';
        toast(
            placed ? `${translate('Added')} "${name}" → ${where}` : `"${name}" ${translate('placed beside')} ${where}`,
            () => ws.undo(false)
        );
    };

    const render = () => {
        const stage = stageBlocks()[currentStage];
        const stageName = stage ? translate(STAGE_TITLE[stage.type]) : '';
        // "For <stage>": the Binary sub-category named after the current stage
        const main = [...toolboxDom.children].find(c => catKey(c) === 'Binary');
        const stageCat = [...main.children].find(
            c => c.tagName === 'category' && stage && STAGE_TITLE[stage.type].startsWith(catKey(c))
        );
        cats.innerHTML = '';
        const chip = (key, text, dot, extra) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('role', 'tab');
            b.setAttribute('aria-selected', selected === key);
            if (extra) b.className = extra;
            if (dot) b.style.setProperty('--dot', dot);
            b.textContent = text;
            b.addEventListener('click', () => {
                selected = key;
                render();
            });
            cats.appendChild(b);
        };
        if (stageCat) chip('stage', `${translate('For')} ${stageName}`, null, 'sug');
        else if (selected === 'stage') selected = catKey(categories[0]);
        categories.forEach(c => {
            const name = catKey(c);
            chip(name, translate(name), TRADE_CATS.includes(name) ? '#a7aeb8' : '#646b76');
        });

        const items =
            selected === 'stage'
                ? blocksIn(stageCat).map(i => ({ ...i, sub: '' }))
                : blocksIn(categories.find(c => catKey(c) === selected));
        hint.innerHTML = '';
        hint.append(`${translate('Tap a block to add it to')} `);
        const b = document.createElement('b');
        b.textContent = stageName;
        hint.append(b);
        list.innerHTML = '';
        items.forEach(item => {
            const li = document.createElement('li');
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'bb3-bk';
            btn.dataset.r = STAGES.includes(item.xml.getAttribute('type')) ? 'stage' : item.role;
            btn.innerHTML = `${SHAPE}<span class="lbl"></span><span class="plus">${PLUS}</span>`;
            const lbl = btn.querySelector('.lbl');
            lbl.textContent = labelOf(item.xml);
            if (item.sub) {
                const small = document.createElement('small');
                small.textContent = translate(item.sub);
                lbl.appendChild(small);
            }
            btn.addEventListener('click', () => add(item));
            li.appendChild(btn);
            list.appendChild(li);
        });
    };

    $id('bb3-m-blocks').addEventListener('click', () => {
        render();
        dialog.showModal();
    });
};

const toolsSheet = () => {
    const dialog = $id('bb3-tools');
    dialog.querySelectorAll('[data-for]').forEach(btn =>
        btn.addEventListener('click', () => {
            dialog.close();
            $id(btn.dataset.for).click();
        })
    );
    $id('bb3-m-tools').addEventListener('click', () => dialog.showModal());
};

const menuSheet = () => {
    const dialog = $id('bb3-more');
    const langs = dialog.querySelector('.bb3-langs');
    const active = document.querySelector('#display_language .language');
    document.querySelectorAll('#select_language li:not(:first-child)').forEach(li => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = li.textContent.trim();
        btn.setAttribute('aria-current', !!active && active.textContent.trim() === btn.textContent);
        btn.addEventListener('click', () => $(li).click());
        langs.appendChild(btn);
    });
    dialog.querySelector('.bb3-disc').textContent = ($id('footer') || {}).textContent.trim();
    $id('bb3-more-btn').addEventListener('click', () => dialog.showModal());
};

export default function initV3() {
    document.body.classList.add('bb3-ready'); // phone shell stays hidden until the app has loaded after login
    // the toolbox Blockly was injected with: no second fetch, no window where the Blocks button is dead
    const toolboxDom = Blockly.mainWorkspace.options.languageTree;
    paintBlocks(toolboxDom);
    blocksSheet(toolboxDom);
    dockTooltips();
    sheets();
    startStop();
    stageNav();
    toolsSheet();
    menuSheet();
    $id('bb3-m-bots').addEventListener('click', () => $id('storeButton').click());
    $id('bb3-m-copy').addEventListener('click', () => $id('copyButton').click());
}
